import { getAuth } from "firebase/auth";

/**
 * Development-specific auth synchronization between localhost:3000 and localhost:3001
 * Uses localStorage and postMessage to share auth state across different ports
 */
export class DevAuthSyncService {
  private static readonly DEV_AUTH_KEY = "dev_cross_port_auth";
  private static readonly MESSAGE_TYPE = "DEV_AUTH_SYNC";

  /**
   * Store current auth state for cross-port access
   */
  static async storeAuthState(): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      try {
        // SECURITY: Store only non-sensitive user data for development
        // Do NOT store ID tokens in localStorage for security
        const authData = {
          uid: user.uid,
          email: user.email,
          // Removed: idToken - security risk
          timestamp: Date.now(),
          expires: Date.now() + 5 * 60 * 1000, // 5 minutes
          // Add session marker for cross-port detection
          sessionActive: true,
        };

        localStorage.setItem(this.DEV_AUTH_KEY, JSON.stringify(authData));

        // Broadcast to other localhost ports (without sensitive tokens)
        this.broadcastAuthState("signin", {
          uid: authData.uid,
          email: authData.email,
          sessionActive: true,
          timestamp: authData.timestamp,
        });

      } catch (error) {
        console.error("Failed to store development auth state:", error);
      }
    }
  }

  /**
   * Check for stored auth state and restore if valid
   */
  static async checkStoredAuthState(): Promise<boolean> {
    try {
      const authDataStr = localStorage.getItem(this.DEV_AUTH_KEY);

      if (!authDataStr) {
        return false;
      }

      const authData = JSON.parse(authDataStr);

      // Check if expired
      if (Date.now() > authData.expires) {
        localStorage.removeItem(this.DEV_AUTH_KEY);
        return false;
      }

      const auth = getAuth();

      // If already signed in with the same user, no need to do anything
      if (auth.currentUser && auth.currentUser.uid === authData.uid) {
        return true;
      }


      // Different localhost ports don't share Firebase Auth state
      // SECURITY: Use secure session detection instead of token storage
      if (!auth.currentUser) {
        try {

          // Check if this indicates an active session on another port
          if (authData.sessionActive) {
            // Store minimal restoration data (no tokens)
            window.localStorage.setItem(
              "dev_restore_auth",
              JSON.stringify({
                uid: authData.uid,
                email: authData.email,
                needsRestore: true,
                timestamp: Date.now(),
                // Indicate this is a cross-port session detection
                crossPortSession: true,
              })
            );

            return true;
          }
        } catch (error) {
          console.error(
            "Development: Failed to prepare auth restoration:",
            error
          );
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error("Development auth check error:", error);
      localStorage.removeItem(this.DEV_AUTH_KEY);
      return false;
    }
  }

  /**
   * Check if there's a pending auth restoration and handle it
   */
  static checkPendingAuthRestore(): { needsRestore: boolean; userData?: any } {
    try {
      const restoreDataStr = localStorage.getItem("dev_restore_auth");
      if (!restoreDataStr) {
        return { needsRestore: false };
      }

      const restoreData = JSON.parse(restoreDataStr);

      // Check if the restore request is recent (within 30 seconds)
      if (Date.now() - restoreData.timestamp > 30000) {
        localStorage.removeItem("dev_restore_auth");
        return { needsRestore: false };
      }

      // Clean up the restore data
      localStorage.removeItem("dev_restore_auth");


      return {
        needsRestore: true,
        userData: restoreData,
      };
    } catch (error) {
      console.error("Development: Error checking pending auth restore:", error);
      localStorage.removeItem("dev_restore_auth");
      return { needsRestore: false };
    }
  }

  /**
   * Clear stored auth state
   */
  static clearAuthState(): void {
    localStorage.removeItem(this.DEV_AUTH_KEY);

    // Broadcast sign-out to other localhost ports
    this.broadcastAuthState("signout", null);

  }

  /**
   * Broadcast auth state changes to other localhost ports
   */
  private static broadcastAuthState(
    action: "signin" | "signout",
    data: any
  ): void {
    const message = {
      type: this.MESSAGE_TYPE,
      action,
      data,
      timestamp: Date.now(),
    };

    // Try to communicate with other localhost ports
    const ports = [3000, 3001];
    const currentPort = window.location.port;

    ports.forEach((port) => {
      if (port.toString() !== currentPort) {
        try {
          // Use localStorage as a simple cross-port communication method
          const channelKey = `${this.MESSAGE_TYPE}_${port}`;
          localStorage.setItem(channelKey, JSON.stringify(message));

          // Clean up after a short delay
          setTimeout(() => {
            localStorage.removeItem(channelKey);
          }, 1000);
        } catch (error) {
        }
      }
    });
  }

  /**
   * Listen for auth state changes from other localhost ports
   */
  static startListening(): () => void {
    const currentPort = window.location.port;
    const channelKey = `${this.MESSAGE_TYPE}_${currentPort}`;

    const checkForMessages = () => {
      try {
        const messageStr = localStorage.getItem(channelKey);
        if (messageStr) {
          const message = JSON.parse(messageStr);

          // Process recent messages only (within last 5 seconds)
          if (Date.now() - message.timestamp < 5000) {
            if (message.action === "signout") {
              const auth = getAuth();
              auth.signOut();
            }
          }

          // Clean up processed message
          localStorage.removeItem(channelKey);
        }
      } catch (error) {
      }
    };

    // Check for messages every second
    const interval = setInterval(checkForMessages, 1000);

    // Return cleanup function
    return () => {
      clearInterval(interval);
    };
  }
}
